/* eslint-disable react/jsx-props-no-spreading */
import { useCallback } from 'react'
import { Experience } from '@ninetailed/experience.js-react'

import PricingCard from '@/components/ui/pricing-card'
import RichText from '@/components/ui/rich-text'
import { mapNtExperiences } from '@/utils/ninetailed'
import { cx } from '@/utils/strings'
import type { Entry } from 'contentful'
import type { TypeSectionSetOfPricingSkeleton } from '@/types/ctf'
import ShieldLoader from '@/components/ui/shield-loader'

type SetOfPricingEntry = Entry<TypeSectionSetOfPricingSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SetOfPricingProps = SetOfPricingEntry['fields'] & {
  hideHeadline?: boolean
  insideTabs?: boolean
}

export function SetOfPricingComponent({
  eyebrowText,
  headline,
  body,
  hideHeadline,
  pricingCards,
  bottomRichText,
  bottomCallout,
  insideTabs,
  highlightTerms,
}: SetOfPricingProps) {
  // Check if any pricing card has multiple action buttons
  const hasMultipleActionCards =
    pricingCards?.some((card) => card?.fields?.actions && card.fields.actions.length > 1) || false

  return (
    <section className={cx('max-w-8xl px-5 lg:px-9 mx-auto mb-0!', !insideTabs && 'py-10')}>
      {(eyebrowText || (headline && !hideHeadline) || body) && (
        <div className="mb-6 lg:mb-10 text-center">
          {eyebrowText && <span className="text-eyebrow uppercase">{eyebrowText}</span>}
          {headline && !hideHeadline && <h3 className="h3 header-gradient">{headline}</h3>}
          {body && (
            <div className="max-w-3xl mx-auto">
              <RichText body={body} />
            </div>
          )}
        </div>
      )}
      {pricingCards && (
        <div
          className={cx(
            'xl:grid mx-auto gap-6 grid-rows-1',
            pricingCards.length === 1 && `max-w-4xl grid-cols-1`,
            pricingCards.length === 2 && `max-w-4xl xl:max-w-6xl grid-cols-2`,
            pricingCards.length === 3 && `grid-cols-3`,
            pricingCards.length === 4 && `grid-cols-4 max-w-8xl`
          )}
        >
          {pricingCards.map((pricingCard, idx) =>
            pricingCard?.fields?.columnHeading ? (
              <div
                className={cx(
                  'hidden xl:block relative px-8 pb-[10px] text-2xl text-center after:content after:block after:h-[15px] after:mb-1 after:border-r-2 after:border-l-2 after:border-t-2 after:rounded-tl-full after:rounded-tr-full after:absolute after:-bottom-[15px] after:left-9 after:right-9',
                  pricingCard?.fields?.columnHeadingColumnSpan === '2' && 'col-span-2',
                  pricingCard?.fields?.columnHeadingColumnSpan === '3' && 'col-span-3',
                  pricingCard?.fields?.columnHeadingColumnSpan === '4' && 'col-span-4'
                )}
                key={`col-header-${idx}`}
              >
                {pricingCard?.fields?.columnHeading}
              </div>
            ) : null
          )}
          {pricingCards.map((pricingCard) =>
            pricingCard ? (
              <PricingCard
                key={pricingCard.sys.id}
                {...pricingCard.fields}
                highlightTerms={highlightTerms as any}
                hasMultipleActionCards={hasMultipleActionCards}
              />
            ) : null
          )}
        </div>
      )}
      <div
        className={cx(
          '[&_p]:text-xs [&_p]:last-of-type:mb-0 max-w-2xl mx-auto text-center mt-5',
          bottomCallout && `mb-8`
        )}
      >
        <RichText body={bottomRichText} />
      </div>
      {bottomCallout && (
        <div className="rounded-lg max-w-6xl m-auto text-center p-9 bg-white dark:bg-secondary-800 [p]:m-0">
          <RichText body={bottomCallout} />
        </div>
      )}
    </section>
  )
}

export default function SetOfPricing({ nt_experiences, id, hideHeadline, insideTabs, ...props }: any): JSX.Element {
  const ShieldBeatLoader = useCallback(
    () => <ShieldLoader containerHeight="700" shieldSize="Large" className="text-bw-deep-blue" />,
    []
  )
  return (
    <Experience
      {...props}
      id={id}
      key={id}
      experiences={mapNtExperiences(nt_experiences)}
      component={SetOfPricingComponent}
      passthroughProps={{ insideTabs, hideHeadline }}
      loadingComponent={ShieldBeatLoader}
    />
  )
}
